import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	FormControlLabel,
	Grid2,
	MenuItem,
	Radio,
	RadioGroup,
	TextField,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import {
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	useAssignRollToRollset,
	useRemoveRollFromRollset,
	useRollChangeReasons,
	useRollsForAssign,
} from "../hooks/useRollshopOperations";
import type { RollsetListItem } from "../models/RollshopOperations";
import {
	COMPACT_DIALOG_SX,
	COMPACT_OPTION_SX,
	EDITABLE_FIELD_SX,
} from "./compactDialog";
import { ReadOnlyField } from "./ReadOnlyField";

export type RollPosition = "TOP" | "BOTTOM" | "1" | "2" | "3" | "4" | "5";

export const rollPositionLabel = (position: RollPosition | null) => {
	if (position === "TOP") return "Top Roll";
	if (position === "BOTTOM") return "Bottom Roll";
	return position ? `Roll ${position}` : "";
};

const STATUS_LABEL: Record<string, string> = {
	BROKEN: "Broken",
	SCRAPPED: "Scrapped",
};

interface Props {
	open: boolean;
	rollset: RollsetListItem | null;
	position: RollPosition | null;
	currentRollId: number | null;
	currentRollName: string | null;
	onClose: () => void;
	onSuccess: () => void;
}

// Legacy displayChangeRollModal: an empty position assigns a roll, a filled one removes it as Free or Scrap.
export const ChangeRollPositionModal = ({
	open,
	rollset,
	position,
	currentRollId,
	currentRollName,
	onClose,
	onSuccess,
}: Props) => {
	const isAssigning = currentRollId == null;

	const [includeScrapped, setIncludeScrapped] = useState(false);
	const [rollId, setRollId] = useState<number | null>(null);
	const [actionType, setActionType] = useState<"FREE" | "SCRAPPED">("FREE");
	const [reasonId, setReasonId] = useState<number | null>(null);
	const [comments, setComments] = useState("");
	const [errorMsg, setErrorMsg] = useState("");

	useEffect(() => {
		if (open) {
			setIncludeScrapped(false);
			setRollId(null);
			setActionType("FREE");
			setReasonId(null);
			setComments("");
			setErrorMsg("");
		}
	}, [open]);

	const rollsQuery = useRollsForAssign(
		rollset?.MillSizeId ?? null,
		isAssigning && open,
	);
	const reasonsQuery = useRollChangeReasons(!isAssigning && open);

	const filteredRolls = useMemo(() => {
		const all = rollsQuery.data ?? [];
		if (includeScrapped) return all;
		return all.filter((r) => r.StatusCode === "FREE");
	}, [rollsQuery.data, includeScrapped]);

	// Legacy offers every reason but the internal one that frees the roll; the roll ends in the reason's status.
	const scrapReasons = useMemo(
		() => (reasonsQuery.data ?? []).filter((r) => r.RollStatusCode !== "FREE"),
		[reasonsQuery.data],
	);
	const selectedReason = scrapReasons.find((r) => r.Id === reasonId) ?? null;

	const { assignRoll, isAssigning: isAssigningPending } =
		useAssignRollToRollset({
			onSuccess,
			onError: (e) => setErrorMsg(e.message),
		});

	const { removeRoll, isRemoving } = useRemoveRollFromRollset({
		onSuccess,
		onError: (e) => setErrorMsg(e.message),
	});

	const isPending = isAssigningPending || isRemoving;

	const canSave = isAssigning
		? rollId != null && rollset?.Id != null && position != null
		: rollset?.Id != null &&
			position != null &&
			(actionType === "FREE" || reasonId != null);

	const handleSave = () => {
		if (!rollset?.Id || !position || !canSave) return;
		setErrorMsg("");
		if (isAssigning && rollId != null) {
			assignRoll({
				rollsetId: rollset.Id,
				rollId,
				rollPosition: position,
			});
		} else if (!isAssigning) {
			removeRoll({
				rollsetId: rollset.Id,
				rollPosition: position,
				newStatusCode: actionType,
				changeReasonId: actionType === "FREE" ? null : reasonId,
				comments: comments.trim() || null,
			});
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DetailHeader
				title={`${isAssigning ? "Assign" : "Remove"} ${rollPositionLabel(position)}`}
				isSaving={isPending}
				onClose={onClose}
			/>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleSave();
				}}
			>
				<DialogContent sx={{ p: 2, ...COMPACT_DIALOG_SX }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<DetailSection title="Roll" icon={<TrackChangesIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField label="Rollset" value={rollset?.Name} />
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<ReadOnlyField
										label="Position"
										value={rollPositionLabel(position)}
									/>
								</Grid2>
								{isAssigning ? (
									<>
										<Grid2 size={12}>
											<RadioGroup
												row
												value={includeScrapped ? "ALL" : "FREE"}
												onChange={(_, v) => {
													setIncludeScrapped(v === "ALL");
													setRollId(null);
												}}
											>
												<FormControlLabel
													value="FREE"
													control={<Radio size="small" />}
													label="Free rolls only"
												/>
												<FormControlLabel
													value="ALL"
													control={<Radio size="small" />}
													label="Include scrapped rolls"
												/>
											</RadioGroup>
										</Grid2>
										<Grid2 size={12}>
											{/* Hundreds of rolls when the rollset has no mill size, so it is searched, not scrolled. */}
											<Autocomplete
												options={filteredRolls}
												value={
													filteredRolls.find((r) => r.Id === rollId) ?? null
												}
												autoHighlight
												size="small"
												fullWidth
												loading={rollsQuery.isFetching}
												getOptionLabel={(r) =>
													r.StatusCode === "SCRAPPED"
														? `${r.Name} (scrapped)`
														: r.Name
												}
												isOptionEqualToValue={(option, current) =>
													option.Id === current.Id
												}
												onChange={(_event, r) => setRollId(r?.Id ?? null)}
												renderOption={(props, r) => {
													const { key, ...rest } = props as typeof props & {
														key: string;
													};
													return (
														<li key={r.Id} {...rest} style={COMPACT_OPTION_SX}>
															{r.StatusCode === "SCRAPPED"
																? `${r.Name} (scrapped)`
																: r.Name}
														</li>
													);
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														sx={EDITABLE_FIELD_SX}
														label="Roll"
														required
														helperText={
															!rollsQuery.isFetching &&
															filteredRolls.length === 0
																? "No rolls available with the current filter"
																: undefined
														}
														slotProps={{ inputLabel: { shrink: true } }}
													/>
												)}
											/>
										</Grid2>
									</>
								) : (
									<>
										<Grid2 size={12}>
											<ReadOnlyField label="Roll" value={currentRollName} />
										</Grid2>
										<Grid2 size={12}>
											<RadioGroup
												row
												value={actionType}
												onChange={(_, v) => {
													setActionType(v as "FREE" | "SCRAPPED");
													setReasonId(null);
												}}
											>
												<FormControlLabel
													value="FREE"
													control={<Radio size="small" />}
													label="Set roll free"
												/>
												<FormControlLabel
													value="SCRAPPED"
													control={<Radio size="small" />}
													label="Scrap roll"
												/>
											</RadioGroup>
										</Grid2>
										{actionType === "SCRAPPED" && (
											<Grid2 size={12}>
												<TextField
													select
													sx={EDITABLE_FIELD_SX}
													size="small"
													fullWidth
													required
													label="Reason"
													value={reasonId == null ? "" : String(reasonId)}
													onChange={(e) =>
														setReasonId(
															e.target.value === ""
																? null
																: Number(e.target.value),
														)
													}
													disabled={reasonsQuery.isFetching}
													helperText={
														selectedReason?.RollStatusCode
															? `The roll will be set as ${
																	STATUS_LABEL[selectedReason.RollStatusCode] ??
																	selectedReason.RollStatusCode
																}`
															: undefined
													}
													slotProps={{ inputLabel: { shrink: true } }}
												>
													{scrapReasons.map((r) => (
														<MenuItem
															key={r.Id}
															value={String(r.Id)}
															sx={COMPACT_OPTION_SX}
														>
															{r.Name}
														</MenuItem>
													))}
												</TextField>
											</Grid2>
										)}
									</>
								)}
							</Grid2>
						</DetailSection>
						{!isAssigning && (
							<DetailSection title="Comments" icon={<ChatBubbleOutlineIcon />}>
								<TextField
									sx={EDITABLE_FIELD_SX}
									label="Comments"
									value={comments}
									onChange={(e) => setComments(e.target.value)}
									fullWidth
									multiline
									minRows={2}
									maxRows={6}
									size="small"
									slotProps={{
										inputLabel: { shrink: true },
										htmlInput: { maxLength: 400 },
									}}
								/>
							</DetailSection>
						)}
						{errorMsg && <Alert severity="error">{errorMsg}</Alert>}
					</Box>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<FooterModalButtons
						isSubmitLoading={isPending}
						disabled={!canSave}
						onHide={onClose}
						buttonColor={isAssigning ? "primary" : "warning"}
						buttonLabel={isAssigning ? "Assign" : "Remove"}
					/>
				</DialogActions>
			</form>
		</Dialog>
	);
};
